import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Download, Plus, X, FileText, Video, Image, File, Edit2, Trash2, ExternalLink } from 'lucide-react';

const FILE_TYPES = [
  { value: 'PDF', label: 'PDF', icon: FileText, color: 'bg-red-100 text-red-600' },
  { value: 'VIDEO', label: 'Video', icon: Video, color: 'bg-purple-100 text-purple-600' },
  { value: 'IMAGE', label: 'Imagen', icon: Image, color: 'bg-green-100 text-green-600' },
  { value: 'OTHER', label: 'Otro', icon: File, color: 'bg-gray-100 text-gray-600' }
];

// Convert Google Drive share link to direct download link
function convertGoogleDriveLink(url) {
  if (!url) return url;
  
  // Match Google Drive share links
  const driveMatch = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    const fileId = driveMatch[1];
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
  return url;
}

export default function DownloadsManager() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', url: '', file_type: 'PDF', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const { data } = await api.get('/admin/downloads');
      setDownloads(data.downloads || []);
    } catch (err) {
      console.error('Error loading downloads:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm({ title: '', description: '', url: '', file_type: 'PDF', sort_order: downloads.length });
    setEditing(null);
    setShowForm(true);
    setError('');
  };

  const openEdit = (item) => {
    setForm({
      title: item.title,
      description: item.description || '',
      url: item.url,
      file_type: item.file_type || 'PDF',
      sort_order: item.sort_order || 0
    });
    setEditing(item.id);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editing) {
        await api.put(`/admin/downloads/${editing}`, form);
      } else {
        await api.post('/admin/downloads', form);
      }
      setShowForm(false);
      loadDownloads();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta descarga?')) return;
    try {
      await api.delete(`/admin/downloads/${id}`);
      loadDownloads();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const toggleActive = async (id, currentActive) => {
    try {
      await api.put(`/admin/downloads/${id}`, { active: !currentActive });
      loadDownloads();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const getFileTypeInfo = (type) => {
    return FILE_TYPES.find(t => t.value === type) || FILE_TYPES[3];
  };

  const isGoogleDrive = (url) => url && url.includes('drive.google.com');

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm">
        <div className="flex items-center justify-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-500">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-black text-gray-900">📥 Gestor de Descargas</h2>
            <p className="text-gray-500 text-sm mt-1">Agrega archivos para que los usuarios descarguen</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg font-bold hover:bg-orange-600 transition shadow-sm"
          >
            <Plus className="w-5 h-5" />
            Nueva Descarga
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-900">
                {editing ? '✏️ Editar Descarga' : '➕ Nueva Descarga'}
              </h3>
              <button 
                onClick={() => setShowForm(false)} 
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Ej: Manual de Servicio Honda CB190R"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={2}
                  placeholder="Descripción opcional"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  URL del archivo *
                  {isGoogleDrive(form.url) && (
                    <span className="ml-2 text-green-600 font-normal">✓ Google Drive</span>
                  )}
                </label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://drive.google.com/uc?id=..."
                  required
                />
                {isGoogleDrive(form.url) && (
                  <p className="text-xs text-green-600 mt-1">
                    Se convertirá automáticamente al formato de descarga directa
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Tipo</label>
                  <select
                    value={form.file_type}
                    onChange={(e) => setForm({ ...form, file_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {FILE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Orden</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm font-medium border border-red-200">
                  ⚠️ {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-primary text-white py-3 rounded-lg font-bold hover:bg-orange-600 transition disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {downloads.length === 0 ? (
          <div className="p-12 text-center">
            <Download className="w-16 h-16 mx-auto text-gray-200 mb-4" />
            <p className="text-xl font-semibold text-gray-500">No hay descargas</p>
            <p className="text-gray-400 mt-2">Crea tu primera descarga</p>
            <button
              onClick={openAdd}
              className="mt-4 text-primary font-medium hover:underline"
            >
              + Agregar primera descarga
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {downloads.map((item) => {
              const typeInfo = getFileTypeInfo(item.file_type);
              const Icon = typeInfo.icon;
              const isDrive = isGoogleDrive(item.url);
              const downloadUrl = isDrive ? convertGoogleDriveLink(item.url) : item.url;
              
              return (
                <div 
                  key={item.id} 
                  className={`p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:bg-gray-50 transition ${!item.active ? 'bg-gray-50 opacity-60' : ''}`}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${typeInfo.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 w-full">
                    <h4 className="font-bold text-gray-900 truncate">{item.title}</h4>
                    <div className="flex items-center gap-2">
                      <a 
                        href={downloadUrl}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline truncate"
                      >
                        {item.url}
                      </a>
                      {isDrive && (
                        <span className="shrink-0 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">
                          Google Drive
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-1">{item.description}</p>
                    )}
                  </div>

                  {/* Type badge */}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${typeInfo.color}`}>
                    {typeInfo.label}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      #{item.sort_order || 0}
                    </span>
                    
                    <button
                      onClick={() => toggleActive(item.id, item.active)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        item.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                      }`}
                    >
                      {item.active ? '✓' : '○'}
                    </button>

                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                      title="Probar enlace"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>

                    <button
                      onClick={() => openEdit(item)}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
